import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, RefreshCw, Split, DollarSign } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDisputesTab() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("open");
  const [selected, setSelected] = useState(null);
  const [splitPct, setSplitPct] = useState(50);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const r = await axios.get(`${API}/admin/disputes${q}`);
      setItems(r.data || []);
    } catch (e) { toast.error("Erro ao carregar disputas"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);
  const resolve = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/disputes/${selected.dispute_id}/resolve`, {
        action,
        admin_note: note,
        split_pct_to_freelancer: Number(splitPct) || 50,
      });
      toast.success(`Disputa resolvida (${action})`);
      setSelected(null); setNote(""); setSplitPct(50);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-disputes-tab">
      <Card className="p-4 flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-amber-500/10 to-red-500/10 border-amber-500/40">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div>
            <div className="font-bold">Disputas de Trabalhos</div>
            <div className="text-[11px] text-muted-foreground">Reveja e resolva com refund, release ou split.</div>
          </div>
        </div>
        <div className="flex gap-2">
          {["open", "resolved", ""].map(s => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              data-testid={`admin-disputes-filter-${s || "all"}`}
              className={`text-xs px-3 py-1 rounded-full border-2 ${statusFilter === s ? "border-primary bg-primary/10" : "border-border"}`}
            >
              {s === "open" ? "Abertas" : s === "resolved" ? "Resolvidas" : "Todas"}
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={load} className="rounded-full gap-1" data-testid="admin-disputes-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Atualizar
          </Button>
        </div>
      </Card>

      {items.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">Sem disputas nesse filtro.</Card>
      )}

      <div className="space-y-2">
        {items.map(d => (
          <Card key={d.dispute_id} className="p-4 space-y-2" data-testid={`admin-dispute-${d.dispute_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{d.opened_by_role === "client" ? "Cliente" : "Freelancer"} abriu</div>
                <div className="font-bold truncate">{d.job_title || d.job_id}</div>
                <div className="text-xs">
                  <span className="text-muted-foreground">Cliente:</span> <strong>{d.client_name}</strong>
                  {" · "}
                  <span className="text-muted-foreground">Freelancer:</span> <strong>{d.freelancer_name}</strong>
                </div>
                <p className="text-sm mt-1 line-clamp-3">{d.reason}</p>
              </div>
              <div className="text-right">
                <Badge className={`${d.status === "open" ? "bg-amber-500" : "bg-green-500"} text-white`}>{d.status}</Badge>
                <div className="text-lg font-bold text-primary mt-1">R$ {Number(d.amount || 0).toFixed(2)}</div>
              </div>
            </div>
            {d.status === "open" && (
              <Button size="sm" onClick={() => { setSelected(d); setSplitPct(50); setNote(""); }} data-testid={`admin-dispute-resolve-${d.dispute_id}`} className="rounded-full gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Resolver
              </Button>
            )}
            {d.status === "resolved" && (
              <div className="text-[11px] text-muted-foreground">
                <strong>{d.resolved_action}</strong> · {new Date(d.resolved_at).toLocaleString()} {d.admin_note && `— "${d.admin_note}"`}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="admin-dispute-resolve-dialog">
          <DialogHeader><DialogTitle>Resolver disputa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              <div><strong>{selected?.job_title}</strong> — R$ {Number(selected?.amount || 0).toFixed(2)}</div>
              <p className="text-xs text-muted-foreground mt-1">{selected?.reason}</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Nota (opcional)</label>
              <Textarea data-testid="admin-dispute-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <Button data-testid="admin-dispute-refund" onClick={() => resolve("refund_client")} disabled={busy} variant="outline" className="rounded-full gap-2 justify-start">
                <RefreshCw className="w-4 h-4 text-blue-500" /> Reembolsar cliente (100%)
              </Button>
              <Button data-testid="admin-dispute-release" onClick={() => resolve("release_freelancer")} disabled={busy} variant="outline" className="rounded-full gap-2 justify-start">
                <DollarSign className="w-4 h-4 text-green-500" /> Liberar para freelancer (100%)
              </Button>
              <div className="p-3 rounded-lg border border-border">
                <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                  <Split className="w-4 h-4 text-purple-500" /> Dividir (split)
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid="admin-dispute-split-pct" type="number" min="0" max="100" value={splitPct} onChange={(e) => setSplitPct(e.target.value)} className="w-20" />
                  <span className="text-xs text-muted-foreground">% para freelancer</span>
                  <Button data-testid="admin-dispute-split-btn" size="sm" onClick={() => resolve("split")} disabled={busy} className="rounded-full ml-auto">Aplicar split</Button>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Freelancer: R$ {(Number(selected?.amount || 0) * Number(splitPct || 0) / 100).toFixed(2)} · Cliente: R$ {(Number(selected?.amount || 0) * (100 - Number(splitPct || 0)) / 100).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
